import type { ConnectedWallet } from '@privy-io/react-auth';
import { useState } from 'react';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';

import { EditIcon } from '@/ui/Icons/EditIcon';

import { formatShortAddress } from '@/utils/address';

import { RecipientModal } from './RecipientModal';

interface ReceivingWalletLineProps {
  chainId: number;
  walletAddress: string | undefined;
  connectedWallet: MatchaConnectedWallet<ConnectedWallet> | undefined;
}

export const ReceivingWalletLine = ({
  chainId,
  walletAddress,
  connectedWallet,
}: ReceivingWalletLineProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);

  const displayAddress = recipientAddress ?? walletAddress;
  const isSendingToSelf = !recipientAddress;

  return (
    <button
      type="button"
      className="flex items-center justify-between w-auto grow-1 -mx-3 px-3 py-1.5 min-h-9 rounded-sm transition-colors duration-[150ms] ease-in-out hover:cursor-pointer hover:bg-token-alphalight-200"
      onClick={() => setIsModalOpen(true)}
      aria-label="Edit receiving wallet"
    >
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'flex items-center text-token-gray-800',
        })}
      >
        Receiving wallet
      </div>
      <div className="relative flex items-center gap-1">
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-800',
          })}
        >
          {isSendingToSelf ? 'Connected' : 'Custom'}
        </span>
        <span className="text-token-gray-800 text-350">·</span>
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-1600',
          })}
        >
          {displayAddress ? formatShortAddress(displayAddress) : '—'}
        </span>

        <EditIcon className="size-4 text-token-gray-800 ml-0.5" aria-hidden />
        <RecipientModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          onClose={() => setIsModalOpen(false)}
          chainId={chainId}
          connectedWallet={connectedWallet}
        />
      </div>
    </button>
  );
};
